import {
  Component,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Heap } from '../interface/heap';
import { AppRoutes, Customer, RouteHistoryItem, SearchType } from '../models';
import { AuthenticationService, EquipmentService } from '../providers';
import { NavService } from '../providers/services/nav.service';
import { ProfileService } from '../providers/services/profile.service';
import { SearchService } from '../providers/services/search.service';
import { ClientBrandingService } from '../../client-branding/client-branding.service';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
  standalone: false,
})
export class NavComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput: any;
  showLogo: WritableSignal<boolean> = signal(false);
  searchPlaceholderText: string;
  subscriptions: Subscription[] = [];
  customerCountSubscriptionName = 'customerCountSubscription';
  languageChangeSubscriptionName = 'languageChangedSubscription';
  routeChangedSubscriptionName = 'routeChangedSubscription';
  searchbarHasFocus: boolean = false;
  timeout: any = null;
  searchType = SearchType.productType;
  selectedCustomers: Array<Customer> = [];
  currentRoute: string;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public searchService: SearchService,
    public profileService: ProfileService,
    public authenticationService: AuthenticationService,
    public equipmentService: EquipmentService,
    public translateService: TranslateService,
    public navService: NavService,
    readonly clientBrandingService: ClientBrandingService
  ) {
    this.subscriptions.push(
      translateService.onDefaultLangChange.subscribe((r) => {
        this.setNavValues();
      })
    );
    this.subscriptions.push(
      this.router.events.subscribe(
        (event) => {
          if (event instanceof NavigationEnd) {
            this.currentRoute = event.url;
            this.setNavValues();
          }
        },
        (err: Error) => {
          console.error(err);
        }
      )
    );
  }

  getSearchbarHasFocus = () => {
    if (this.searchbarHasFocus) {
      return 'searchbar-active searchbar-has-value searchbar-has-focus';
    }
    return '';
  };

  clearSearchText = (hasFocus = true) => {
    this.searchInput.nativeElement.value = '';
    this.searchbarHasFocus = false;
    this.getSearchbarHasFocus();
    if (hasFocus) {
      this.searchInput.nativeElement.focus();
    }
  };

  trackHeapEvent(eventName: string, propertydata: any) {
    try {
      ((window as any).heap as Heap).track(eventName, propertydata); // we keep property data (such as which dropdown item a user selected) as passed in unadorned with additional 'rdo%' properties
    } catch (e) {}
  }

  // TODO: It would be better to move most of this page specific logic in the route pages themselves.
  setNavValues = () => {
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
    this.searchPlaceholderText = this.translateService.instant(
      'main.search.equipment_placeholder'
    );
    this.navService.showSearchbar = true;
    this.navService.showFilters = true;
    this.clearAllSearchResults();
    switch (this.currentRoute) {
      case AppRoutes.categories:
        this.navService.showBackButton = false;
        this.navService.pageTitle = this.translateService.instant(
          'main.page_titles.rental_equipment'
        );
        this.searchType = SearchType.productType;
        this.navService.clearRouteHistory();
        break;
      case AppRoutes.settings:
        this.navService.showBackButton = false;
        this.navService.pageTitle = this.translateService.instant(
          'main.page_titles.profile_settings'
        );
        this.navService.showSearchbar = false;
        this.navService.showFilters = false;
        this.navService.clearRouteHistory();
        break;
      case AppRoutes.filters:
        // Handled within the page component
        break;
      case AppRoutes.filter:
        // Handled within the page component.
        break;
      case AppRoutes.restricted:
        this.navService.showBackButton = false;
        this.navService.pageTitle = this.translateService.instant(
          'main.page_titles.rental_equipment'
        );
        this.navService.showSearchbar = false;
        this.navService.showFilters = false;
        this.navService.clearRouteHistory();
        break;
      case AppRoutes.clientSelection:
        this.navService.showBackButton = true;
        this.navService.pageTitle = this.translateService.instant(
          'main.page_titles.rental_equipment'
        );
        this.searchType = SearchType.client;
        this.searchPlaceholderText = this.translateService.instant(
          'main.search.client_placeholder'
        );
        this.navService.showFilters = false;
        break;
      case AppRoutes.locationSettings:
        this.navService.showBackButton = true;
        this.navService.pageTitle = this.translateService.instant(
          'main.page_titles.geography_selection'
        );
        this.navService.showFilters = false;
        this.navService.showSearchbar = false;
        break;
      case AppRoutes.estimateSettings:
        this.navService.showBackButton = true;
        this.navService.pageTitle = `${this.translateService.instant('main.mobile.estimate_button')} ${this.translateService.instant('main.mobile.rate_type')}`;
        this.navService.showFilters = false;
        this.navService.showSearchbar = false;
        break;
      case AppRoutes.productSortSettings:
        this.navService.showBackButton = true;
        this.navService.pageTitle = this.translateService.instant(
          'main.page_titles.product_types_sort'
        );
        this.navService.showFilters = false;
        this.navService.showSearchbar = false;
        break;
      case AppRoutes.customerSelection:
        this.navService.showBackButton = true;
        this.navService.pageTitle = this.translateService.instant(
          'main.page_titles.customer_selection'
        );
        this.searchType = SearchType.customer;
        this.searchPlaceholderText = this.translateService.instant(
          'main.search.customers_placeholder'
        );
        this.navService.showFilters = true;
        break;
      default: {
        this.navService.showBackButton = true;
        this.searchType = SearchType.productType;
        break;
      }
    }
    this.showLogo.set(
      this.currentRoute === AppRoutes.categories ||
        this.currentRoute === AppRoutes.settings
    );
    this.updateBackButtonData(this.currentRoute);
    this.trackHeapEvent(
      `Web MQA : ${this.navService.pageTitle} : Visited`,
      null
    );
  };

  clearCustomerSelections = () => {
    this.profileService.clearCustomers();
  };

  getLabelTranslation = (value: string, replacements?: Array<string>) => {
    return this.profileService.getLabelTranslation(value, replacements);
  };

  displaySearchBar = () => {
    return this.navService.showSearchbar;
  };

  showFilterBar = () => {
    return this.navService.showFilters;
  };

  getCustomerFilterText = () => {
    if (this.selectedCustomers.length > 1) {
      return this.getLabelTranslation('Customers_selected', [
        this.selectedCustomers.length.toString(),
      ]);
    } else {
      return `${this.getLabelTranslation('Customer')} ${this.profileService.selectedCustomers[0].customer_name}`;
    }
  };

  updateBackButtonData = (url: string) => {
    let route = url;

    if (!route.startsWith(AppRoutes.settings)) {
      const urlItems = url.split('/');
      if (urlItems.length > 1) {
        route = '/' + urlItems[1];
      }
    }

    this.navService.currentRoute = route;
    const appRoute = new RouteHistoryItem();
    appRoute.CurrentRoute = this.navService.currentRoute;
    appRoute.Url = url;
    appRoute.PageTitle = this.navService.pageTitle;
    const previousRoute =
      this.navService.routeHistory[this.navService.routeHistory.length - 1];
    if (!previousRoute || appRoute.Url !== previousRoute.Url) {
      this.navService.routeHistory.push(appRoute);
    }
  };

  ngOnInit(): void {
    this.subscriptions.push(
      this.profileService.selectedCustomersChanged.subscribe((r) => {
        this.selectedCustomers = r;
        this.navService.showFilters = r.length > 0;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s: Subscription) => {
      s.unsubscribe();
    });
  }

  pageHasSearchResults = () => {
    return (
      this.profileService.searchedCustomers ||
      this.searchService.searchResults ||
      this.profileService.searchedClients
    );
  };

  clearAllSearchResults = () => {
    this.profileService.searchedClients = undefined;
    this.profileService.searchedCustomers = undefined;
    this.searchService.searchResults = undefined;
  };

  clearSearchResults = () => {
    switch (this.searchType) {
      case SearchType.customer:
        this.profileService.searchCustomers('');
        break;
      case SearchType.client:
        this.profileService.searchClients('');
        break;
      default:
        this.searchService.clearSearchResults();
    }
    this.clearSearchText(false);
  };

  onSearchInput(event: any): void {
    if (event.target.value && event.target.value.length > 0) {
      this.searchbarHasFocus = true;
      const timeoutDuration =
        this.searchType !== SearchType.client ? 1000 : 250;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        switch (this.searchType) {
          case SearchType.customer:
            this.profileService.searchCustomers(event.target.value);
            break;
          case SearchType.client:
            this.profileService.searchClients(event.target.value);
            break;
          default:
            this.searchService.search(event.target.value);
        }
      }, timeoutDuration);
    } else {
      clearTimeout(this.timeout);
      this.searchbarHasFocus = false;
      this.clearSearchResults();
    }
  }
}
